import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type { ComboboxItem } from '../Combobox/Combobox'
import { SearchSelect } from '../SearchSelect/SearchSelect'

/**
 * Odoo Many2one wire shape — `[id, name]`. Pickers accept `null` for
 * unset; the page-level wire shape (OdooM2O) uses `false` — pages adapt
 * at the boundary.
 */
export type PickerValue = readonly [number, string] | null

interface ProductRow {
  id: number
  name: string
  default_code: string
  /** Optional second line (e.g. Sample Orders: INCI · family). */
  subtitle?: string
  /** Optional on-hand quantity (Sample Orders: sample-room stock). */
  in_stock_qty?: number
}

interface ProductsResponse {
  products: ProductRow[]
  /** Optional total matches before the limit cap — drives the "N of M" hint. */
  total?: number
}

function describe(p: ProductRow): string | undefined {
  const parts: string[] = []
  if (p.subtitle) parts.push(p.subtitle)
  if (typeof p.in_stock_qty === 'number') {
    parts.push(p.in_stock_qty > 0 ? `${formatQty(p.in_stock_qty)} in stock` : 'none in stock')
  }
  return parts.length ? parts.join(' — ') : undefined
}

function formatQty(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '')
}

export interface ProductPickerProps {
  value: PickerValue
  onChange: (next: PickerValue) => void
  placeholder?: string
  /**
   * When true, the popover opens immediately on mount and closing via
   * outside-click / Esc calls `onCancel`. Used by
   * `EditableGrid.renderEditor` — the cell is already in edit mode and
   * the user shouldn't have to click a second time to expand the picker.
   */
  editorMode?: boolean
  onCancel?: () => void
  /**
   * Size of the popover panel. Defaults to 320px wide.
   */
  width?: number
  className?: string
  disabled?: boolean
  /**
   * When true, products with `default_code` starting `SMPL` or `SAMP`
   * are filtered out at the search endpoint. Used by Quotes (samples
   * are giveaways, never quoted).
   */
  excludeSamples?: boolean
  /**
   * Override the search endpoint. Defaults to
   * `/api/pricelists/search/products`. Any endpoint used here must return
   * the same `{ products: [{ id, name, default_code }] }` shape — e.g.
   * Sample Orders' `/api/sample-orders/search/products`, which scopes
   * results to SPI's own sample-only SKUs instead of the full catalog.
   */
  searchEndpoint?: string
  /** Extra query params sent with every search (e.g. `{ family: 'Volatile Fluids' }`). */
  extraParams?: Record<string, string | undefined>
  /** Results per search. Defaults to 20. */
  limit?: number
}

/**
 * Debounced server-search picker over `/api/pricelists/search/products`.
 * Items render as `name` primary + `default_code` as a monospace meta
 * line. SPI-prefix products are server-sorted first — UX contract from
 * Sprint 01 D-1b's `PricelistEditor`.
 */
export function ProductPicker({
  value,
  onChange,
  placeholder = 'Search products…',
  editorMode = false,
  onCancel,
  width = 320,
  className,
  disabled,
  excludeSamples = false,
  searchEndpoint = '/api/pricelists/search/products',
  extraParams,
  limit = 20,
}: ProductPickerProps) {
  const [query, setQuery] = useState<string>('')
  const [debounced, setDebounced] = useState<string>('')

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query), 200)
    return () => clearTimeout(id)
  }, [query])

  const { data, isFetching } = useQuery<ProductsResponse>({
    queryKey: [
      'ui',
      'product-picker',
      searchEndpoint,
      debounced,
      excludeSamples,
      limit,
      JSON.stringify(extraParams ?? {}),
    ],
    queryFn: () => {
      const params = new URLSearchParams({
        q: debounced,
        limit: String(limit),
      })
      if (excludeSamples) params.set('exclude_samples', '1')
      for (const [k, v] of Object.entries(extraParams ?? {})) {
        if (v) params.set(k, v)
      }
      return jsonFetch<ProductsResponse>(
        `${searchEndpoint}?${params}`,
      )
    },
    staleTime: 30_000,
  })

  const items: ComboboxItem[] = useMemo(
    () =>
      (data?.products ?? []).map((p) => ({
        value: String(p.id),
        label: p.name || p.default_code || `Product ${p.id}`,
        meta: p.default_code || undefined,
        description: describe(p),
        keywords: p.default_code ? [p.default_code] : undefined,
      })),
    [data],
  )

  const handlePicked = (idStr: string) => {
    const id = Number(idStr)
    const row = (data?.products ?? []).find((p) => p.id === id)
    if (!row) return
    onChange([id, row.name || row.default_code])
  }

  const emptyMessage = isFetching ? 'Searching…' : debounced ? 'No products found' : 'Type to search'
  const shown = data?.products.length ?? 0
  const total = data?.total
  const footer =
    typeof total === 'number' && total > shown
      ? `Showing ${shown} of ${total} — keep typing to narrow`
      : undefined

  return (
    <SearchSelect
      variant="cell"
      items={items}
      value={value ? String(value[0]) : undefined}
      selectedLabel={value ? value[1] : null}
      onQueryChange={setQuery}
      onSelect={handlePicked}
      onClear={() => onChange(null)}
      editorMode={editorMode}
      onCancel={onCancel}
      width={width}
      placeholder={placeholder}
      emptyMessage={emptyMessage}
      shouldFilter={false}
      disabled={disabled}
      className={className}
      footer={footer}
    />
  )
}
